import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import RouteErrorBoundary from '../../view/components/RouteErrorBoundary';
import SplashScreen from '../../view/components/SplashScreen';
import SidebarLayout from '../layout/SidebarLayout';
import { pagePath } from '../utils/consts/pagePath';
import { AuthGuard } from './AuthGuard';
import { LandingOrAppRedirect } from './LandingOrAppRedirect';
import { catalogRoutes } from './catalog';
import { contractTemplateRoutes } from './contractTemplate';
import { leadRoutes } from './lead';
import { eventRoutes } from './event';
import { settingsRoutes } from './settings';
import { teamRoutes } from './team';
import { tenantRoutes } from './tenant';
import { todoRoutes } from './todo';

const Home = lazy(() => import('../../view/pages/HomePage'));
const BillingPage = lazy(() => import('../../view/pages/BillingPage'));
const ReportsPage = lazy(() => import('../../view/pages/ReportsPage'));
const StatisticsPage = lazy(() => import('../../view/pages/StatisticsPage'));
const Login = lazy(() => import('../../view/pages/LoginPage'));
const SignupPage = lazy(() => import('../../view/pages/SignupPage'));
const ConfirmSignupPage = lazy(
  () => import('../../view/pages/ConfirmSignupPage'),
);
const ForgotPasswordPage = lazy(
  () => import('../../view/pages/ForgotPasswordPage'),
);
const ResetPasswordPage = lazy(
  () => import('../../view/pages/ResetPasswordPage'),
);
const NotFoundPage = lazy(() => import('../../view/pages/NotFoundPage'));
const PlanChoicePage = lazy(() => import('../../view/pages/PlanChoicePage'));
const AcceptInvitationPage = lazy(
  () => import('../../view/pages/AcceptInvitationPage'),
);
const PublicSignPage = lazy(() => import('../../view/pages/PublicSignPage'));
const PublicLeadFormPage = lazy(
  () => import('../../view/pages/PublicLeadFormPage'),
);
const PublicGalleryPage = lazy(
  () => import('../../view/pages/PublicGalleryPage'),
);
const ClientPortalPage = lazy(
  () => import('../../view/pages/ClientPortalPage'),
);

function LazyFallback() {
  return <SplashScreen isLoading />;
}

function PublicRoutes() {
  return (
    <AuthGuard isPrivate={false}>
      <Suspense fallback={<LazyFallback />}>
        <Outlet />
      </Suspense>
    </AuthGuard>
  );
}

function ProtectedRoutes() {
  return (
    <AuthGuard isPrivate>
      <Suspense fallback={<LazyFallback />}>
        <Outlet />
      </Suspense>
    </AuthGuard>
  );
}

function RoutesWithSideNavbar() {
  return (
    <SidebarLayout>
      <Outlet />
    </SidebarLayout>
  );
}

const router = createBrowserRouter([
  {
    path: pagePath.landing,
    errorElement: <RouteErrorBoundary />,
    element: <LandingOrAppRedirect />,
  },
  {
    path: pagePath.invite,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<LazyFallback />}>
        <AcceptInvitationPage />
      </Suspense>
    ),
  },
  {
    path: pagePath.sign,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<LazyFallback />}>
        <PublicSignPage />
      </Suspense>
    ),
  },
  {
    path: pagePath.publicLeadForm,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<LazyFallback />}>
        <PublicLeadFormPage />
      </Suspense>
    ),
  },
  {
    path: pagePath.publicGallery,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<LazyFallback />}>
        <PublicGalleryPage />
      </Suspense>
    ),
  },
  {
    path: pagePath.clientPortal,
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<LazyFallback />}>
        <ClientPortalPage />
      </Suspense>
    ),
  },
  {
    element: <PublicRoutes />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: pagePath.login,
        element: <Login />,
      },
      {
        path: pagePath.signup,
        element: <SignupPage />,
      },
      {
        path: pagePath.confirmSignup,
        element: <ConfirmSignupPage />,
      },
      {
        path: pagePath.forgotPassword,
        element: <ForgotPasswordPage />,
      },
      {
        path: pagePath.resetPassword,
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    element: <ProtectedRoutes />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <RoutesWithSideNavbar />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: pagePath.home,
            element: <Home />,
          },
          {
            path: pagePath.billing,
            element: <BillingPage />,
          },
          {
            path: pagePath.reports,
            element: <ReportsPage />,
          },
          {
            path: pagePath.statistics,
            element: <StatisticsPage />,
          },
          leadRoutes,
          eventRoutes,
          todoRoutes,
          catalogRoutes,
          contractTemplateRoutes,
          settingsRoutes,
          teamRoutes,
        ],
      },
      tenantRoutes,
      {
        path: pagePath.planChoice,
        element: <PlanChoicePage />,
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LazyFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export default function Router() {
  return <RouterProvider router={router} />;
}
